import { toast } from 'react-toastify';

import { BackendRoute } from '../../../../config/index';
import { QueryKey } from '../../../../constants/index';
import { useAppMutation, useInvalidateQueries, useLocale } from '../../../../hooks/index';
import ApiService from '../../../../services/api-service.util';
import { SigningMode } from '../enums/template.const';
import {
  type AddTemplateFormSchema,
  getStepThreeFile,
  getStepThreeFileLink,
} from '../validations/add-template.validation';

export type CreateTemplateResponse = {
  category: string;
  companyId: string;
  createdAt: string;
  docusignTemplateId: null | string;
  equityPlanId: string;
  expirationPeriod: null | string;
  fieldMapping: null | Record<string, string>;
  fileLink: string;
  id: string;
  name: string;
  status: string;
  subCategory: string;
  updatedAt: string;
};

const buildCreateTemplateBody = (data: AddTemplateFormSchema): Record<string, unknown> => {
  const { stepOne, stepTwo, stepThree } = data;
  const fileLink = getStepThreeFileLink(stepThree.file);
  const file = getStepThreeFile(stepThree.file);
  if (!fileLink) throw new Error('File link is required');

  return {
    equityPlanId: stepOne.equityPlan ?? '',
    fileLink,
    documentName: file?.name,
    name: stepOne.name,
    category: stepOne.category ?? '',
    subCategory: stepOne.subCategory ?? '',
    ...(stepOne.expireDocument && stepOne.expirationPeriod != null
      ? { expirationPeriod: String(stepOne.expirationPeriod) }
      : {}),
    signingOrderMatters: stepTwo.signingMode === SigningMode.SET_ORDER,
    signatoryTypes: stepTwo.signatories,
    fieldMapping: stepThree.fieldMapping ?? {},
    tabs: (stepThree.tabs ?? []).map((tab) => ({
      ...tab,
      signerStakeholderId: tab.value ?? undefined,
    })),
  };
};

export const useCreateTemplate = () => {
  const { invalidateQuery } = useInvalidateQueries(QueryKey.GET_TEMPLATES);
  const { messagesLocale } = useLocale();

  const {
    isPending,
    mutate: createTemplate,
    data,
  } = useAppMutation<CreateTemplateResponse, Error, AddTemplateFormSchema>(
    [QueryKey.CREATE_TEMPLATE],
    {
      mutationFn: (data) => {
        const body = buildCreateTemplateBody(data);
        return ApiService.post<CreateTemplateResponse>(
          { endpoint: BackendRoute.TEMPLATES },
          { body },
        );
      },
      onSuccess: () => {
        toast.success(messagesLocale['TEMPLATE_ADDED_SUCCESSFULLY']);
        invalidateQuery();
      },
    },
  );

  return { createTemplate, isPending, data };
};
